import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { toast } from 'react-hot-toast';
import type { CartItem } from '../types';
import { cartApi } from '../api';
import { mapCartResponseToItems, persistCartSnapshot } from '../utils/cartMapper';

interface CartContextType {
  cart: CartItem[];
  /** Re-fetch server cart for `zoniq_cart_event_id` (or clear if empty). */
  refreshCartFromBackend: () => Promise<void>;
  addToCart: (item: CartItem) => Promise<void>;
  removeFromCart: (cartItemId: number) => Promise<void>;
  clearCart: (eventId?: string) => Promise<void>;
  cartTotal: number;
  sessionTimeLeft: number;
  isSessionActive: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function secondsUntil(iso: string | null): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor(t / 1000) - Math.floor(Date.now() / 1000));
}

/** The slot ids of a cart line, used to recognise "the same selection" when it is added twice. */
function slotKey(slotIds: (number | string | null | undefined)[]): string {
  return slotIds.filter((s) => s != null).map(String).sort().join(',');
}

async function addActivityLine(token: string, item: CartItem, eventIdNum: number): Promise<Record<string, unknown>> {
  const { eventId: _ignored, ...selection } = item.activitySelection!;
  const payload = { eventId: eventIdNum, ...selection, quantity: item.quantity };
  try {
    return (await cartApi.addItem(token, payload)) as Record<string, unknown>;
  } catch (e) {
    // Already in the cart: set that line's quantity instead
    if (!(e instanceof Error) || !e.message.includes('already in your cart')) throw e;
    const current = await cartApi.getCurrentCart(token, item.eventId);
    const wanted = slotKey(
      selection.picks?.map((p) => p.activitySlotId) ?? [selection.activitySlotId]
    );
    const existing = current?.items?.find((row: Record<string, unknown>) => {
      const acts = (row.activities as Record<string, unknown>[]) ?? [];
      return slotKey(acts.map((a) => (a.activitySlotId ?? a.activity_slot_id) as number)) === wanted;
    }) as { id?: number } | undefined;
    if (!existing?.id) throw e;
    return (await cartApi.updateItem(token, String(existing.id), { quantity: item.quantity })) as Record<string, unknown>;
  }
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);

  const applyServerCart = useCallback((raw: unknown) => {
    const mapped = mapCartResponseToItems(raw as Record<string, unknown> | null);
    setCart(mapped.items);
    persistCartSnapshot(mapped.items, mapped.eventId, mapped.expiresAtIso);
    setSessionTimeLeft(secondsUntil(mapped.expiresAtIso));
  }, []);

  const refreshCartFromBackend = useCallback(async () => {
    const token = localStorage.getItem('zoniq_token');
    const auth = localStorage.getItem('zoniq_auth');
    if (auth !== 'true' || !token) return;
    const eventId = localStorage.getItem('zoniq_cart_event_id') || undefined;
    try {
      const raw = await cartApi.getCurrentCart(token, eventId);
      if (raw && Array.isArray(raw.items) && raw.items.length > 0) {
        applyServerCart(raw);
      } else {
        setCart([]);
        persistCartSnapshot([], null, null);
        setSessionTimeLeft(0);
      }
    } catch (e) {
      console.error('refreshCartFromBackend', e);
    }
  }, [applyServerCart]);

  useEffect(() => {
    const bootstrap = async () => {
      const auth = localStorage.getItem('zoniq_auth');
      const token = localStorage.getItem('zoniq_token');

      try {
        const rawCart = localStorage.getItem('zoniq_cart');
        if (rawCart) {
          const parsed = JSON.parse(rawCart) as CartItem[];
          if (Array.isArray(parsed) && parsed.length > 0 && parsed.some((i) => !i.cartItemId)) {
            localStorage.removeItem('zoniq_cart');
            localStorage.removeItem('zoniq_session_time');
            localStorage.removeItem('zoniq_cart_event_id');
          }
        }
      } catch {
        /* ignore */
      }

      if (auth === 'true' && token) {
        await refreshCartFromBackend();
      } else {
        setCart([]);
        setSessionTimeLeft(0);
      }
    };

    void bootstrap();

    const onStorage = () => {
      void bootstrap();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshCartFromBackend]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (sessionTimeLeft > 0 && cart.length > 0) {
      timer = setInterval(() => {
        setSessionTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setCart([]);
            persistCartSnapshot([], null, null);
            const token = localStorage.getItem('zoniq_token');
            const eid = localStorage.getItem('zoniq_cart_event_id') || undefined;
            if (token) {
              cartApi.clearCart(token, eid).catch(console.error);
            }
            toast.error(
              'Your ticket hold session has expired. The tickets have been released back to the public.',
              { duration: 6000 }
            );
            window.location.href = '/events';
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [sessionTimeLeft, cart.length]);

  const addToCart = useCallback(
    async (item: CartItem) => {
      const token = localStorage.getItem('zoniq_token');
      if (!token || localStorage.getItem('zoniq_auth') !== 'true') {
        toast.error('Please sign in to book tickets.');
        throw new Error('AUTH_REQUIRED');
      }

      const eventIdNum = parseInt(item.eventId, 10);
      if (Number.isNaN(eventIdNum)) {
        toast.error('Invalid event.');
        throw new Error('INVALID_EVENT');
      }

      // Activities / bundles: one cart line per selection, matched by its slots
      if (item.activitySelection) {
        try {
          const raw = await addActivityLine(token, item, eventIdNum);
          applyServerCart(raw);
          return;
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : 'Could not update cart';
          toast.error(msg);
          throw e;
        }
      }

      try {
        let current = await cartApi.getCurrentCart(token, item.eventId);
        const findLine = () =>
          current?.items?.find((row: Record<string, unknown>) => {
            const cid = row.seatCategoryId ?? row.seat_category_id;
            const oid = row.occurrenceId ?? row.occurrence_id;
            // Same tier on a different date/slot is a distinct line (passes have no occurrence)
            return (
              String(cid) === item.tierId &&
              (oid == null ? '' : String(oid)) === (item.occurrenceId ?? '')
            );
          }) as { id?: number } | undefined;

        let existingLine = findLine();
        let raw: Record<string, unknown>;

        if (existingLine?.id) {
          raw = (await cartApi.updateItem(token, String(existingLine.id), {
            quantity: item.quantity,
          })) as Record<string, unknown>;
        } else {
          const payload: {
            eventId: number;
            seatCategoryId: number;
            occurrenceId?: number;
            quantity: number;
            seatIds?: number[];
          } = {
            eventId: eventIdNum,
            seatCategoryId: parseInt(item.tierId, 10),
            quantity: item.quantity,
          };
          if (item.occurrenceId) payload.occurrenceId = parseInt(item.occurrenceId, 10);
          if (item.seatIds?.length) payload.seatIds = item.seatIds;

          try {
            raw = (await cartApi.addItem(token, payload)) as Record<string, unknown>;
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : '';
            if (msg.includes('Update quantity instead')) {
              current = await cartApi.getCurrentCart(token, item.eventId);
              existingLine = findLine();
              if (existingLine?.id) {
                raw = (await cartApi.updateItem(token, String(existingLine.id), {
                  quantity: item.quantity,
                })) as Record<string, unknown>;
              } else {
                throw e;
              }
            } else {
              throw e;
            }
          }
        }

        applyServerCart(raw);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Could not update cart';
        if (msg !== 'AUTH_REQUIRED' && msg !== 'INVALID_EVENT') {
          toast.error(msg);
        }
        throw e;
      }
    },
    [applyServerCart]
  );

  const removeFromCart = useCallback(
    async (cartItemId: number) => {
      const token = localStorage.getItem('zoniq_token');
      const line = cart.find((i) => i.cartItemId === cartItemId);
      const eventId = line?.eventId || localStorage.getItem('zoniq_cart_event_id') || undefined;

      if (token) {
        try {
          await cartApi.deleteItem(token, String(cartItemId));
          const raw = await cartApi.getCurrentCart(token, eventId);
          if (raw && Array.isArray(raw.items) && raw.items.length > 0) {
            applyServerCart(raw);
          } else {
            setCart([]);
            persistCartSnapshot([], null, null);
            setSessionTimeLeft(0);
          }
        } catch (e) {
          console.error(e);
          toast.error('Could not remove item from cart.');
        }
      } else {
        setCart((prev) => prev.filter((i) => i.cartItemId !== cartItemId));
        persistCartSnapshot(
          cart.filter((i) => i.cartItemId !== cartItemId),
          localStorage.getItem('zoniq_cart_event_id'),
          null
        );
      }
    },
    [cart, applyServerCart]
  );

  const clearCart = useCallback(async (eventId?: string) => {
    const token = localStorage.getItem('zoniq_token');
    const eid = eventId || localStorage.getItem('zoniq_cart_event_id') || undefined;
    setCart([]);
    setSessionTimeLeft(0);
    persistCartSnapshot([], null, null);
    if (token) {
      try {
        await cartApi.clearCart(token, eid);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  const cartTotal = cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const isSessionActive = sessionTimeLeft > 0 && cart.length > 0;

  const contextValue = useMemo(
    () => ({
      cart,
      refreshCartFromBackend,
      addToCart,
      removeFromCart,
      clearCart,
      cartTotal,
      sessionTimeLeft,
      isSessionActive,
    }),
    [
      cart,
      refreshCartFromBackend,
      addToCart,
      removeFromCart,
      clearCart,
      cartTotal,
      sessionTimeLeft,
      isSessionActive,
    ]
  );

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
